import {
  required,
  Edit,
  SimpleForm,
  SimpleFormIterator,
  ArrayInput,
  NumberInput,
  ReferenceInput,
  SelectInput,
  useNotify,
  useRedirect,
} from "react-admin";
import { Grid } from "@mui/material";
import { useParams } from "react-router";
import InactiveDeleteOptionEditToolbar from "features/EditToolbar";
import { AdministrationServicePath, SPECIALITIES, SELECT_ALL, CitizenshipIds, AnnualFeeModes } from "utils/constants";
import { getPermissions } from "auth/security/securityUtils";
import { checkPermission } from "server/security/utils";
import permissions from "app/permissions";
import { settings } from "app";
import BackButtonToolbar from "features/BackButtonToolbar";
import { administrationRequests } from "api/administrationApi";
import AnnualFeePlansHistoryDialog from "./AnnualFeePlansHistoryDialog";
import AdmissionYearInput from "./Partials/AdmissionYearInput";
import PaidEducationFeeFields from "./Partials/PaidEducationFeeFields";
import { euroFormat, euroParse, resolveFeeMode } from "./utils";

const ManageAnnualFees = () => {
  const { id } = useParams();
  const userPermissions = getPermissions();
  const canManageAnnualFees = checkPermission(userPermissions, permissions.canManageAnnualFees);
  const notify = useNotify();
  const redirect = useRedirect();
  return (
    <Edit
      title="Управление на годишни такси"
      mutationMode="pessimistic"
      actions={<BackButtonToolbar additionalButton={<AnnualFeePlansHistoryDialog specialityId={id} />} />}
      transform={data => {
        return {
          ...data,
          stateEducationFees: data.stateEducationFees.map(item => ({
            ...item,
            studyTypeId: item.studyTypeId === SELECT_ALL ? null : item.studyTypeId,
          })),
          paidEducationFees: data.paidEducationFees.map(item => {
            const isBulgarian = item.citizenship === CitizenshipIds.Bulgarians;
            const {
              feeMode, // UI-only
              instructionLanguageId,
              fixedAmountEuro,
              baseNormEuro,
              stateFeeEuro,
              professionalFieldCoefficient,
              compositeAssessmentCoefficient,
              discountAmountEuro,
              ...rest
            } = item;
            const isFixedAmount = !isBulgarian || resolveFeeMode(feeMode, item) === AnnualFeeModes.Fixed;
            return {
              ...rest,
              studyTypeId: item.studyTypeId === SELECT_ALL ? null : item.studyTypeId,
              admissionReasonId: item.admissionReasonId === SELECT_ALL ? null : item.admissionReasonId,
              ...(isBulgarian
                ? {}
                : {
                    instructionLanguageId: item.instructionLanguageId === SELECT_ALL ? null : instructionLanguageId,
                  }),
              ...(isFixedAmount
                ? { fixedAmountEuro }
                : {
                    baseNormEuro,
                    stateFeeEuro,
                    professionalFieldCoefficient,
                    compositeAssessmentCoefficient,
                    discountAmountEuro,
                  }),
            };
          }),
        };
      }}
      mutationOptions={{
        onSuccess: data => {
          redirect(`/${AdministrationServicePath}${SPECIALITIES}`);
          notify("Успешна редакция на годишни такси", { type: "success" });
        },
      }}
    >
      <SimpleForm
        spacing={4}
        toolbar={<InactiveDeleteOptionEditToolbar />}
      >
        <Grid
          container
          spacing={2}
        >
          <AdmissionYearInput disabled={!canManageAnnualFees} />
        </Grid>

        <Grid
          container
          spacing={2}
        >
          <ArrayInput
            disabled
            source="stateEducationFees"
            label="Държавна поръчка"
            defaultValue={[{}]}
          >
            <SimpleFormIterator
              disableReordering
              inline
              disableAdd={!canManageAnnualFees}
              disableClear
              sx={settings.style.iteratorWithoutGap}
            >
              <Grid
                container
                spacing={2}
              >
                <Grid
                  item
                  xs={12}
                  md={4}
                >
                  <ReferenceInput
                    source="studyTypeId"
                    reference={administrationRequests.getStudyTypesSelect()}
                    label="Вид на обучение"
                  >
                    <SelectInput
                      optionText="name"
                      label="Вид на обучение *"
                      disabled={!canManageAnnualFees}
                      defaultValue={SELECT_ALL}
                      emptyValue={SELECT_ALL}
                      emptyText={"Всички"}
                      fullWidth
                    />
                  </ReferenceInput>
                </Grid>

                <Grid
                  item
                  xs={12}
                  md={2}
                >
                  <NumberInput
                    source="priceEuro"
                    label="Цена в евро"
                    disabled={!canManageAnnualFees}
                    format={euroFormat}
                    parse={euroParse}
                    validate={required()}
                  />
                </Grid>
              </Grid>
            </SimpleFormIterator>
          </ArrayInput>
        </Grid>

        <Grid
          container
          spacing={2}
        >
          <ArrayInput
            source="paidEducationFees"
            label="Платено обучение"
            defaultValue={[{}]}
          >
            <SimpleFormIterator
              disableReordering
              inline
              disableClear
              disableAdd={!canManageAnnualFees}
              sx={settings.style.iteratorWithoutGap}
            >
              <PaidEducationFeeFields disabled={!canManageAnnualFees} />
            </SimpleFormIterator>
          </ArrayInput>
        </Grid>
      </SimpleForm>
    </Edit>
  );
};
export default ManageAnnualFees;
